/**
 * Email validation utilities
 */

// Characters allowed in local-part (before @)
const LOCAL_PART_ALLOWED = /^[a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~.]+$/;

// Characters allowed in domain-part (after @)
const DOMAIN_PART_ALLOWED = /^[a-zA-Z0-9.-]+$/;

// Full email validation including structure and character rules
const EMAIL_REGEX = /^[a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~.]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

export interface EmailValidationResult {
  isValid: boolean;
  errors: string[];
}

export function validateEmail(email: string): EmailValidationResult {
  const errors: string[] = [];
  
  // Trim and lowercase the email
  email = email.trim().toLowerCase();

  if (!email) {
    return {
      isValid: false,
      errors: ['Email address is required']
    };
  }

  // Check basic email structure
  if (!email.includes('@')) {
    return {
      isValid: false,
      errors: ['Email must contain @ symbol']
    };
  }

  const [localPart, domainPart] = email.split('@');

  // Check local-part rules
  if (!localPart) {
    errors.push('Username part (before @) is required');
  } else {
    if (localPart.length > 64) {
      errors.push('Username part cannot be longer than 64 characters');
    }
    if (!LOCAL_PART_ALLOWED.test(localPart)) {
      errors.push('Username contains invalid characters');
    }
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      errors.push('Username cannot start or end with a period');
    }
    if (localPart.includes('..')) {
      errors.push('Username cannot contain consecutive periods');
    }
  }

  // Check domain-part rules
  if (!domainPart) {
    errors.push('Domain part (after @) is required');
  } else {
    if (domainPart.length > 255) {
      errors.push('Domain part cannot be longer than 255 characters');
    }
    if (!DOMAIN_PART_ALLOWED.test(domainPart)) {
      errors.push('Domain contains invalid characters');
    }
    if (domainPart.startsWith('-') || domainPart.endsWith('-')) {
      errors.push('Domain cannot start or end with a hyphen');
    }
    if (!domainPart.includes('.')) {
      errors.push('Domain must contain at least one period');
    }
    if (!/\.[a-zA-Z]{2,}$/.test(domainPart)) {
      errors.push('Domain must end with a valid top-level domain (e.g., .com, .org)');
    }
  }

  // Full email validation
  if (!EMAIL_REGEX.test(email)) {
    if (errors.length === 0) {
      errors.push('Invalid email format');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Sanitizes an email input by removing invalid characters in real-time
 * @param input Raw input string
 * @returns Sanitized email string
 */
export function sanitizeEmailInput(input: string): string {
  // Remove any characters that aren't allowed in either part
  return input.replace(/[^a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~.@]/g, '');
}
